import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Download, Trophy, Share2, LogIn } from 'lucide-react';
import { PollarBrandMini } from '@/components/PollarBrand';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SpeedRunSummary = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(true);
  
  const copyShareLink = () => {
    const shareUrl = window.location.href;
    navigator.clipboard.writeText(shareUrl)
      .then(() => {
        toast.success('Link copied to clipboard!');
      })
      .catch(() => {
        toast.error('Failed to copy link');
      });
  };

  useEffect(() => {
    fetchSummary();
  }, [sessionId]);

  const fetchSummary = async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setIsLoggedIn(false);
      }
      
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/speed-run/${sessionId}/summary`, { headers });
      setSummary(response.data);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load summary');
      setLoading(false);
    }
  };

  const handleDownloadCSV = () => {
    if (!summary) return;

    // Build CSV content
    const headers = ['Participant Name', 'Question #', 'Answer', 'Correct', 'Points', 'Time (seconds)'];
    let csvContent = headers.join(',') + '\n';

    summary.participants.forEach(participant => {
      participant.answers.forEach((answer, idx) => {
        const row = [
          `"${participant.participant_name}"`,
          idx + 1,
          `"${answer.answer}"`,
          answer.is_correct ? 'Yes' : 'No',
          answer.points_awarded,
          answer.time_taken.toFixed(2)
        ];
        csvContent += row.join(',') + '\n';
      });
    });

    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `speed-run-quiz-${sessionId}-results.csv`;
    link.click();
    window.URL.revokeObjectURL(url);

    toast.success('Results downloaded!');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-lg text-gray-600">Loading summary...</p>
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-lg text-gray-600">Summary not found</p>
      </div>
    );
  }

  const session = summary.session;

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef3 100%)' }}>
      {/* Top Bar */}
      <div className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <PollarBrandMini />
              <Button
                onClick={() => {
                  const referrer = sessionStorage.getItem('referrer') || '/dashboard';
                  navigate(isLoggedIn ? referrer : '/login');
                }}
                variant="ghost"
                size="sm"
              >
                {isLoggedIn ? (
                  <>
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back
                  </>
                ) : (
                  <>
                    <LogIn className="mr-2 h-4 w-4" />
                    Login
                  </>
                )}
              </Button>
              
              {/* Share Link Button */}
              <Button
                onClick={copyShareLink}
                variant="outline"
                size="sm"
              >
                <Share2 className="mr-2 h-4 w-4" />
                Share
              </Button>
            </div>
            
            <Button
              onClick={handleDownloadCSV}
              className="bg-gradient-to-r from-orange-500 to-red-600"
            >
              <Download className="mr-2 h-4 w-4" />
              Download Results
            </Button>
          </div>
        </div>
      </div>

      {/* Summary Content */}
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Header */}
        <div className="text-center mb-8">
          <Trophy className="h-16 w-16 text-orange-500 mx-auto mb-4" />
          <h1 className="text-4xl font-bold mb-2">{session.topic}</h1>
          <div className="inline-block px-4 py-2 bg-orange-100 text-orange-600 rounded-full font-semibold">
            Speed Run Quiz Summary
          </div>
        </div>

        {/* Overview Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <Card>
            <CardContent className="p-6 text-center">
              <p className="text-sm text-gray-600 mb-2">Total Participants</p>
              <p className="text-4xl font-bold text-blue-600">{summary.total_participants}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6 text-center">
              <p className="text-sm text-gray-600 mb-2">Total Questions</p>
              <p className="text-4xl font-bold text-orange-600">{session.questions?.length || 0}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6 text-center">
              <p className="text-sm text-gray-600 mb-2">Winner</p>
              <p className="text-2xl font-bold text-yellow-600">
                {summary.participants[0]?.participant_name || 'N/A'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Question Statistics */}
        <Card className="mb-8">
          <CardContent className="p-6">
            <h2 className="text-2xl font-bold mb-6">Question Statistics</h2>
            
            <div className="space-y-4">
              {session.questions?.map((question, idx) => {
                // Calculate how many participants got this question right
                let correctCount = 0;
                let totalAnswered = 0;
                
                summary.participants.forEach(participant => {
                  const answer = participant.answers[idx];
                  if (answer) {
                    totalAnswered++;
                    if (answer.is_correct) {
                      correctCount++;
                    }
                  }
                });
                
                const correctPercentage = totalAnswered > 0 
                  ? Math.round((correctCount / totalAnswered) * 100)
                  : 0;
                
                // Find the correct answer text from options
                const correctOption = question.options?.find(opt => opt.id === question.correct_answer);
                const correctAnswerText = correctOption?.text || question.correct_answer;
                
                return (
                  <div key={question.id || idx} className="border rounded-lg p-4">
                    <div className="mb-3">
                      {/* Question Header with Number and Points */}
                      <div className="flex items-center gap-2 mb-3">
                        <span className="inline-block px-2 py-1 bg-orange-100 text-orange-600 text-xs font-semibold rounded">
                          Question {idx + 1}
                        </span>
                        <span className="text-sm text-gray-600">
                          {question.points} points
                        </span>
                      </div>
                      
                      {/* Question Text */}
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">
                        {question.question_text || 'No question text'}
                      </h3>
                      
                      {/* Correct Answer and Percentage */}
                      <div className="flex items-start justify-between">
                        <p className="text-sm text-green-600">
                          ✓ Correct Answer: <span className="font-semibold">{correctAnswerText}</span>
                        </p>
                        <div className="text-right ml-4">
                          <p className="text-3xl font-bold text-orange-600">{correctPercentage}%</p>
                          <p className="text-xs text-gray-500">got it right</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="relative">
                      <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-green-500 to-green-600 transition-all duration-500"
                          style={{ width: `${correctPercentage}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-600 mt-1">
                        {correctCount} of {totalAnswered} participants answered correctly
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Participant Results */}
        <Card>
          <CardContent className="p-6">
            <h2 className="text-2xl font-bold mb-6">Participant Results</h2>
            
            <div className="space-y-6">
              {summary.participants.map((participant, idx) => (
                <div key={participant.participant_id} className="border-b pb-6 last:border-b-0">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-4">
                      <div className="text-3xl font-bold text-gray-400">#{idx + 1}</div>
                      <div>
                        <h3 className="text-xl font-bold">{participant.participant_name}</h3>
                        <p className="text-sm text-gray-600">
                          {participant.correct_answers}/{participant.questions_answered} correct
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-600">Total Score</p>
                      <p className="text-3xl font-bold text-orange-600">{participant.total_score}</p>
                    </div>
                  </div>

                  {/* Answer Details */}
                  <div className="ml-12 space-y-2">
                    {participant.answers.map((answer, answerIdx) => (
                      <div 
                        key={answer.id || answerIdx}
                        className={`flex items-center justify-between p-3 rounded-lg ${
                          answer.is_correct ? 'bg-green-50' : 'bg-red-50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <span className="font-semibold text-gray-700">Q{answerIdx + 1}:</span>
                          <span className="text-gray-800">{answer.answer}</span>
                          {answer.is_correct ? (
                            <span className="text-green-600 font-semibold">✓ Correct</span>
                          ) : (
                            <span className="text-red-600 font-semibold">✗ Wrong</span>
                          )}
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-sm text-gray-600">
                            {answer.time_taken.toFixed(2)}s
                          </span>
                          <span className={`font-bold ${
                            answer.points_awarded > 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {answer.points_awarded > 0 ? '+' : ''}{answer.points_awarded}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SpeedRunSummary;
